import { cloneDeep, set } from 'lodash';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import React, { useEffect, useRef, useState } from 'react';
import usePagebuilder from '../../../../../hooks/pagebuilder/usePagebuilder';
import { useCustomTranslations } from '../../../../../hooks/useCustomTranslations';
import { HTMLComponentInterface, UiComponentElementInterface } from '../../../../../interfaces/pagebuilder';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import { richTextContentFormat } from '../../../../../services/helper';
import { linkify } from '../../../../../utils/functions';
import EmptyComponent from '../../../pageBuilder/EmptyComponent';
import { TitleAndDescription } from '../../../pageBuilder/TitleAndDescription';

const VyRichTextEditor = dynamic(() => import('../../../../common/VyRichTextEditor'), {
  ssr: false,
  loading: () => <p>loading...</p>,
});

const HTMLComponent = ({ uiComponent }: UiComponentElementInterface<HTMLComponentInterface>) => {
  const templateEditMode = useSelector((state) => state.common.templateEditMode);
  const event = useSelector((state) => state.common.event);
  const user = useSelector((state) => state.common.user);
  const page = useSelector((state) => state.common.pagedata);

  const [editMode, setEditMode] = useState(false);

  const { ct } = useCustomTranslations();
  const { editComponent, toTextVariable } = usePagebuilder();
  const { t } = useTranslation();
  const wrapperRef = useRef<any>(null);

  useEffect(() => {
    !templateEditMode && setEditMode(false);
  }, [templateEditMode]);

  function getItemPlacement(): string {
    switch (uiComponent?.alignment) {
      case 'left':
        return 'place-items-start';
      case 'center':
        return 'place-items-center';
      case 'right':
        return 'place-items-end';
      default:
        return '';
    }
  }

  function calculateWidth(): string {
    const width = (uiComponent?.customStyling?.richTextWidth || 1) * 100;
    return `${width}%`;
  }

  const updateProp = (value: string) => {
    const component = cloneDeep(uiComponent!);
    set(component, 'content', value);
    editComponent(component);
    setEditMode(false);
  };

  const onDoubleClick = () => {
    if (!templateEditMode) return;
    setEditMode(true);
  };

  function replaceVariables(text: string) {
    if (templateEditMode) return uiComponent?.content;
    if (!event || !uiComponent?.content) return;
    if (!text.includes('{{')) return uiComponent.content;

    const registrationFields = user?.registrations?.find((registration) => registration.event === event?._id)?.registrationFields;

    const formattedRegistrationFields = registrationFields?.map((registrationField) => {
      return {
        varName: toTextVariable(registrationField.field.label),
        value: registrationField.field.isURLField ? linkify(registrationField.value, 'hover:underline') : registrationField.value,
      };
    });

    const regex = /{{\s*(.*?)\s*}}/g; // Regular expression to match {{var_name}}
    return text.replace(regex, (match, varName) => {
      const fieldValue = formattedRegistrationFields?.find((field) => field.varName === toTextVariable(varName))?.value;

      // Replace variable with value, fallback value or blank if not found
      return fieldValue ?? uiComponent.fallbackValues?.find((fallback) => fallback.variable === toTextVariable(varName))?.value ?? '';
    });
  }

  return (
    <div
      className={`w-full overflow-hidden`}
      style={{
        color: uiComponent?.styling?.textColor || '#000',
      }}>
      <TitleAndDescription component={uiComponent!} />
      <div ref={wrapperRef} className={`w-full relative grid ${getItemPlacement()} ${editMode ? 'p-2' : ''}`} onDoubleClick={onDoubleClick}>
        {editMode && (
          <div className="w-full">
            <VyRichTextEditor
              key={`inline-richText-${uiComponent?._id}`}
              defaultValue={uiComponent?.content || ''}
              allowedFonts={event?.organization?.allowedFonts}
              height={400}
              inline
              isCompact
              bottomToolbar
              isHTMLComponent
              component={uiComponent}
              onBlur={(val: any) => updateProp(val)}
            />
          </div>
        )}
        {!editMode && (
          <>
            {uiComponent?.content ? (
              <div
                className={`pt-2 grid max-w-[100cqw] break-words`}
                style={{ width: calculateWidth() }}
                dangerouslySetInnerHTML={{
                  __html:
                    uiComponent && (uiComponent.content || (uiComponent.translations && uiComponent.translations?.length > 0))
                      ? richTextContentFormat(
                          `<style type="text/css">.contentWrap > ol{list-style-type: revert} .contentWrap > ul{list-style-type: revert}</style><div class="contentWrap" >${ct(
                            page?.team ? uiComponent.content.toString() : replaceVariables(uiComponent.content.toString()),
                            uiComponent
                          )}</div>`
                        )
                      : ``,
                }}
              />
            ) : (
              <>{templateEditMode && <EmptyComponent componentName={t('pagebuilder:html-double-click')} />}</>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HTMLComponent;
